import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, UserPlus, Trash2, Crown, User, BookOpen, Languages, Pencil, Check, X } from 'lucide-react';
import { useState } from 'react';
import { teamsApi } from '../api/teams';
import type { TeamMember } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import UserBadge from '../components/UserBadge';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { useAuth } from '../contexts/useAuth';

export default function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user, isAdmin } = useAuth();

  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'member' | 'owner'>('member');
  const [addMemberError, setAddMemberError] = useState<string | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const { data: team, isLoading, error } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamsApi.getById(teamId!),
    enabled: !!teamId,
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { email: string; role: 'member' | 'owner' }) =>
      teamsApi.addMember(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsAddMemberModalOpen(false);
      setMemberEmail('');
      setMemberRole('member');
      setAddMemberError(null);
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 404) {
        setAddMemberError('Пользователь с таким email не найден');
      } else if (status === 409) {
        setAddMemberError('Этот пользователь уже в команде');
      } else {
        setAddMemberError('Не удалось добавить участника');
      }
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => teamsApi.removeMember(teamId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: (data: { userId: string; role: 'member' | 'owner' }) =>
      teamsApi.updateMemberRole(teamId!, data.userId, data.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: () => teamsApi.delete(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      navigate('/teams');
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      teamsApi.update(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsEditMode(false);
    },
  });

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    setAddMemberError(null);
    addMemberMutation.mutate({ email: memberEmail.trim(), role: memberRole });
  };

  const handleRemoveMember = (member: TeamMember) => {
    if (window.confirm(`Удалить ${member.username || member.email} из команды?`)) {
      removeMemberMutation.mutate(member.id);
    }
  };

  const handleToggleRole = (member: TeamMember) => {
    const newRole = member.role === 'owner' ? 'member' : 'owner';
    const action = newRole === 'owner' ? 'повысить до владельца' : 'понизить до участника';
    if (window.confirm(`Вы уверены, что хотите ${action} ${member.username || member.email}?`)) {
      updateRoleMutation.mutate({ userId: member.id, role: newRole });
    }
  };

  const handleStartEdit = () => {
    setEditName(team?.name ?? '');
    setEditDescription(team?.description ?? '');
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleSaveEdit = () => {
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    updateTeamMutation.mutate({
      name: trimmedName,
      description: editDescription.trim() || undefined,
    });
  };

  const handleDeleteTeam = () => {
    if (window.confirm(`Вы уверены, что хотите удалить команду "${team?.name}"? Проекты команды станут без привязки к командам.`)) {
      deleteTeamMutation.mutate();
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Ошибка загрузки команды" />;
  if (!team) return <ErrorMessage message="Команда не найдена" />;

  const isOwner = team.members?.some(m => m.role === 'owner' && m.id === user?.id) ?? false;
  const canManage = isOwner || isAdmin;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/teams">
          <Button variant="secondary" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад к командам
          </Button>
        </Link>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/60 p-5">
        {isEditMode ? (
          <div className="space-y-3">
            <Input
              label="Название команды"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Название команды"
            />
            <Textarea
              label="Описание (опционально)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Описание команды"
              rows={3}
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSaveEdit}
                disabled={!editName.trim() || updateTeamMutation.isPending}
              >
                <Check className="h-4 w-4 mr-2" />
                Сохранить
              </Button>
              <Button
                variant="secondary"
                onClick={handleCancelEdit}
                disabled={updateTeamMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Отмена
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {team.name}
              </h1>
              {team.description && (
                <p className="text-gray-500 dark:text-gray-400 mb-2">
                  {team.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <UserBadge username={team.created_by_username} size="md" />
                {team.created_at && (
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    {formatRelativeTime(team.created_at)}
                  </span>
                )}
              </div>
            </div>
            {canManage && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="secondary"
                  onClick={handleStartEdit}
                  className="flex-1 sm:flex-initial flex items-center justify-center"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Редактировать
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDeleteTeam}
                  disabled={deleteTeamMutation.isPending}
                  className="flex-1 sm:flex-initial flex items-center justify-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Удалить
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Участники" className="hover:shadow-md transition-shadow">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Участников: {team.members?.length ?? 0}
            </p>
            {canManage && (
              <Button
                size="sm"
                onClick={() => setIsAddMemberModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Добавить
              </Button>
            )}
          </div>

          {team.members && team.members.length > 0 ? (
            <div className="space-y-2">
              {team.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      {member.role === 'owner' ? (
                        <Crown className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {member.username ? `@${member.username}` : member.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {member.role === 'owner' ? 'Владелец' : 'Участник'}
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleRole(member)}
                        disabled={updateRoleMutation.isPending}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                        title={member.role === 'owner' ? 'Понизить до участника' : 'Повысить до владельца'}
                      >
                        <Crown className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveMember(member)}
                        disabled={removeMemberMutation.isPending}
                        className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Удалить из команды"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Нет участников
            </p>
          )}
        </Card>

        <Card title="Проекты команды" className="hover:shadow-md transition-shadow">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Проектов: {team.series?.length ?? 0}
          </p>

          {team.series && team.series.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {team.series.map((s) => (
                <Link
                  key={s.id}
                  to={`/series/${s.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {s.title}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Languages className="h-3 w-3" />
                          {s.source_language?.toUpperCase()}
                        </span>
                        <UserBadge username={s.created_by_username} showIcon={false} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Нет проектов в команде
            </p>
          )}
        </Card>
      </div>

      <Modal
        isOpen={isAddMemberModalOpen}
        onClose={() => {
          setIsAddMemberModalOpen(false);
          setAddMemberError(null);
          setMemberEmail('');
        }}
        title="Добавить участника"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddMemberModalOpen(false);
                setAddMemberError(null);
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={!memberEmail.trim() || addMemberMutation.isPending}
            >
              Добавить
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddMember} className="space-y-4">
          {addMemberError && <ErrorMessage message={addMemberError} />}
          <Input
            label="Email пользователя"
            type="email"
            value={memberEmail}
            onChange={(e) => setMemberEmail(e.target.value)}
            required
            placeholder="user@example.com"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Роль
            </label>
            <select
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value as 'member' | 'owner')}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
            >
              <option value="member">Участник</option>
              <option value="owner">Владелец</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
